import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import ScrollToTop from "@/components/base/ScrollToTop";
import { articleContent } from "@/mocks/articleData";
import { articlesList } from "@/mocks/categoryData";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? articleContent[id] : null;

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-24 flex items-center justify-center">
          <div className="text-center">
            <i className="ri-file-unknow-line text-5xl text-foreground-300 mb-4 block"></i>
            <p className="text-foreground-500 mb-4">文章未找到</p>
            <Link to="/category/articles" className="text-primary-600 hover:text-primary-700 text-sm font-medium cursor-pointer">
              返回文章列表
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articlesList.filter((a) => a.id !== article.id).slice(0, 3);

  const breadcrumbItems = [
    { label: "香氛文章", path: "/category/articles" },
    { label: article.title },
  ];

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-24">
        <article>
          {/* Article Header */}
          <header className="w-full px-6 md:px-10 py-8 md:py-12 bg-background-50">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />
              <div className="mt-8">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-xs px-3 py-1 bg-accent-100 text-accent-700 rounded-full font-medium">
                    {article.category}
                  </span>
                  <span className="text-xs text-foreground-400">{article.readTime}</span>
                </div>
                <h1 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-900 mb-4 leading-tight">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 text-sm text-foreground-400">
                  <span>{article.author}</span>
                  <span>·</span>
                  <span>{article.date}</span>
                </div>
              </div>
            </div>
          </header>

          {/* Featured Image */}
          <div className="w-full px-6 md:px-10 py-4 bg-background-50">
            <div className="max-w-4xl mx-auto">
              <div className="relative aspect-[2/1] rounded-lg overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                  title={article.title}
                />
              </div>
            </div>
          </div>

          {/* Article Content */}
          <section className="w-full px-6 md:px-10 py-10 md:py-14 bg-background-50">
            <div className="max-w-3xl mx-auto">
              <div className="prose-custom">
                {article.content.map((block, index) => {
                  if (block.type === "heading" && block.text) {
                    return (
                      <h2 key={index} className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mt-10 mb-4">
                        {block.text}
                      </h2>
                    );
                  }
                  if (block.type === "paragraph" && block.text) {
                    return (
                      <p key={index} className="text-sm md:text-base text-foreground-600 leading-relaxed mb-5">
                        {block.text}
                      </p>
                    );
                  }
                  return null;
                })}
              </div>

              {/* Article Footer */}
              <div className="mt-14 pt-8 border-t border-background-200/60">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-sm text-foreground-500">标签：</span>
                  <span className="text-xs px-3 py-1.5 bg-secondary-100 text-secondary-700 rounded-full cursor-pointer hover:bg-secondary-200 transition-colors">
                    东方香氛
                  </span>
                  <span className="text-xs px-3 py-1.5 bg-secondary-100 text-secondary-700 rounded-full cursor-pointer hover:bg-secondary-200 transition-colors">
                    香氛文化
                  </span>
                  <span className="text-xs px-3 py-1.5 bg-secondary-100 text-secondary-700 rounded-full cursor-pointer hover:bg-secondary-200 transition-colors">
                    媚肉之香
                  </span>
                </div>
              </div>
            </div>
          </section>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="w-full px-6 md:px-10 py-12 md:py-16 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-8">
                相关文章
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group bg-background-50 rounded-lg overflow-hidden transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                  >
                    <div className="relative h-44 overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        title={ra.title}
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-xs px-2 py-1 bg-accent-100 text-accent-700 rounded-full font-medium">
                          {ra.category}
                        </span>
                      </div>
                      <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {ra.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "首页", item: "https://meirouzhixiang.com" },
                  { "@type": "ListItem", position: 2, name: "香氛文章", item: "https://meirouzhixiang.com/category/articles" },
                  { "@type": "ListItem", position: 3, name: article.title },
                ],
              },
              {
                "@type": "Article",
                headline: article.title,
                author: { "@type": "Organization", name: article.author },
                datePublished: article.date,
                description: article.content[0]?.type === "paragraph" ? article.content[0].text : article.title,
                inLanguage: "zh-CN",
              },
              {
                "@type": "WebPage",
                url: `https://meirouzhixiang.com/article/${article.id}`,
                name: `${article.title} - 媚肉之香`,
                description: article.content[0]?.type === "paragraph" ? article.content[0]?.text?.slice(0, 160) || article.title : article.title,
              },
            ],
          }),
        }}
      />
    </>
  );
}